import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Activity, Search, Filter, Download, MapPin, Clock, Zap } from 'lucide-react';
import { MapContainer, TileLayer, Polyline, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const ActivitiesMonitoring = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  
  const [filters, setFilters] = useState({
    search: '',
    city: '',
    date_from: '',
    date_to: ''
  });

  useEffect(() => {
    fetchActivities();
  }, [pagination.page, filters]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      });
      
      const response = await axios.get(`/activities?${params}`);
      
      // Safely handle the response - ensure we always have an array
      if (response.data && Array.isArray(response.data.activities)) {
        setActivities(response.data.activities);
        setPagination(prev => ({
          ...prev,
          total: response.data.total || 0,
          totalPages: response.data.totalPages || 1
        }));
      } else if (response.data && Array.isArray(response.data)) {
        // Handle case where activities are at the root level
        setActivities(response.data);
        setPagination(prev => ({
          ...prev,
          total: response.data.length || 0,
          totalPages: 1
        }));
      } else {
        console.warn('Unexpected API response structure:', response.data);
        setActivities([]);
        setPagination(prev => ({
          ...prev,
          total: 0,
          totalPages: 1
        }));
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
      // Always set to empty array on error to prevent map crashes
      setActivities([]);
      setPagination(prev => ({
        ...prev,
        total: 0,
        totalPages: 1
      }));
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityDetails = async (activityId) => {
    try {
      const response = await axios.get(`/activities/${activityId}`);
      setSelectedActivity(response.data);
      setShowMap(true);
    } catch (error) {
      console.error('Error fetching activity details:', error);
    }
  };

  const exportActivities = async () => {
    try {
      const params = new URLSearchParams({
        format: 'csv',
        ...filters
      });
      window.open(`/export/activities?${params}`, '_blank');
    } catch (error) {
      console.error('Error exporting activities:', error);
    }
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Activities Monitoring</h2>
          <p className="text-gray-600 mt-2">Monitor and analyze user activities</p>
        </div>
        <button
          onClick={exportActivities}
          className="btn-primary flex items-center"
        >
          <Download className="h-5 w-5 mr-2" />
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search activities..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="input-field pl-10"
            />
          </div>
          
          <select
            value={filters.city}
            onChange={(e) => setFilters(prev => ({ ...prev, city: e.target.value }))}
            className="input-field"
          >
            <option value="">All Cities</option>
            <option value="Akure">Akure</option>
            <option value="Lagos">Lagos</option>
            <option value="Abuja">Abuja</option>
          </select>
          
          <input
            type="date"
            value={filters.date_from}
            onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
            className="input-field"
            placeholder="From Date"
          />
          
          <input
            type="date"
            value={filters.date_to}
            onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
            className="input-field"
            placeholder="To Date"
          />
        </div>
      </div>

      {/* Activities Table */}
      <div className="card">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : !activities || activities.length === 0 ? (
          <div className="text-center py-12">
            <Activity className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No activities found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {filters.search || filters.city || filters.date_from || filters.date_to
                ? 'Try adjusting your filters to see more activities.'
                : 'Activities will appear here once users start recording them.'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="table-header">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Activity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User & Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Performance
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration & Pace
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {activities.map((activity) => (
                    <tr key={activity.id} className="table-row">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                            <Activity className="h-5 w-5 text-green-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {activity.name || 'Activity'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {activity.weather_summary}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{activity.user_name}</div>
                        <div className="text-sm text-gray-500">{activity.user_city}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {activity.steps?.toLocaleString()} steps
                        </div>
                        <div className="text-sm text-gray-500">
                          {activity.distance_km?.toFixed(2)} km • {activity.calories} cal
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDuration(activity.duration || 0)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {activity.avg_speed_kmh?.toFixed(1)} km/h avg
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(activity.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => fetchActivityDetails(activity.id)}
                          className="text-blue-600 hover:text-blue-900 flex items-center"
                        >
                          <MapPin className="h-4 w-4 mr-1" />
                          View Map
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-700">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page >= pagination.totalPages}
                  className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Activity Map Modal */}
      {showMap && selectedActivity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Activity Map - {selectedActivity.name || 'Activity'}
                </h3>
                <button
                  onClick={() => setShowMap(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              {/* Activity Details */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {selectedActivity.steps?.toLocaleString() || 0}
                  </div>
                  <div className="text-sm text-gray-600">Steps</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {selectedActivity.distance_km?.toFixed(2) || 0}
                  </div>
                  <div className="text-sm text-gray-600">Distance (km)</div>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-orange-600">
                    {selectedActivity.calories || 0}
                  </div>
                  <div className="text-sm text-gray-600">Calories</div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {selectedActivity.elevation_gain?.toFixed(0) || 0}m
                  </div>
                  <div className="text-sm text-gray-600">Elevation</div>
                </div>
              </div>

              {/* Map */}
              <div className="h-96 rounded-lg overflow-hidden">
                {selectedActivity.path_data && Array.isArray(selectedActivity.path_data) && selectedActivity.path_data.length > 0 ? (
                  <MapContainer
                    center={[
                      selectedActivity.path_data[0].lat,
                      selectedActivity.path_data[0].lng
                    ]}
                    zoom={15}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    <Polyline
                      positions={selectedActivity.path_data.map(point => [point.lat, point.lng])}
                      color="blue"
                      weight={4}
                    />
                    <Marker position={[
                      selectedActivity.path_data[0].lat,
                      selectedActivity.path_data[0].lng
                    ]}>
                      <Popup>Start</Popup>
                    </Marker>
                    <Marker position={[
                      selectedActivity.path_data[selectedActivity.path_data.length - 1].lat,
                      selectedActivity.path_data[selectedActivity.path_data.length - 1].lng
                    ]}>
                      <Popup>End</Popup>
                    </Marker>
                  </MapContainer>
                ) : (
                  <div className="flex items-center justify-center h-full bg-gray-100">
                    <div className="text-gray-500">No GPS data available for this activity</div>
                  </div>
                )}
              </div>

              {/* Additional Details */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">User Information</h4>
                  <div className="space-y-2 text-sm">
                    <div><strong>Name:</strong> {selectedActivity.user_name}</div>
                    <div><strong>City:</strong> {selectedActivity.user_city}</div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Activity Details</h4>
                  <div className="space-y-2 text-sm">
                    <div><strong>Duration:</strong> {formatDuration(selectedActivity.duration || 0)}</div>
                    <div><strong>Avg Pace:</strong> {selectedActivity.avg_pace_kmh?.toFixed(1)} km/h</div>
                    <div><strong>Weather:</strong> {selectedActivity.weather_summary || 'N/A'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivitiesMonitoring; 